import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import type { FeatureTrend } from '@/types/feedback';
import type { VoteKind } from '@/lib/vote';
import { bucketRatings, halfSplitDelta } from '@/lib/trend';
import { Badge } from '@/components/ui/badge';
import { Sparkline } from './charts/Sparkline';
import { DeltaBadge } from './DeltaBadge';
import { SentimentBar } from './SentimentBar';

/**
 * One row per feature: a stacked positive/negative sentiment bar, average
 * rating, response count, a 6-bucket rating sparkline and a period delta.
 * Sorted by response volume (most-heard features first).
 */
export function FeatureLeaderboard({
  features,
  onFeatureSelect,
  onSentimentSelect,
}: {
  features: FeatureTrend[];
  onFeatureSelect?: (feature: string) => void;
  onSentimentSelect?: (kind: VoteKind) => void;
}) {
  // Highest average rating first ("top" feature → least); response volume breaks ties.
  const rows = useMemo(
    () =>
      [...features].sort(
        (a, b) => b.stats.averageRating - a.stats.averageRating || b.stats.count - a.stats.count
      ),
    [features]
  );

  // The most-used feature (highest response count) gets a "Most used" badge, since
  // the list is sorted by rating and the busiest feature may not be at the top.
  const topUsedFeature = useMemo(
    () =>
      features.length > 0
        ? features.reduce((max, f) => (f.stats.count > max.stats.count ? f : max)).stats.feature
        : null,
    [features]
  );

  return (
    <Card data-testid="feature-leaderboard">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Feature leaderboard</CardTitle>
      </CardHeader>
      <CardContent>
        {rows.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">No feature data in this period</p>
        ) : (
          <div className="divide-y">
            {rows.map((f, i) => (
              <LeaderboardRow
                key={f.stats.feature}
                rank={i + 1}
                trend={f}
                isTopUsed={f.stats.feature === topUsedFeature}
                onFeatureSelect={onFeatureSelect}
                onSentimentSelect={onSentimentSelect}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LeaderboardRow({
  rank,
  trend,
  isTopUsed = false,
  onFeatureSelect,
  onSentimentSelect,
}: {
  rank: number;
  trend: FeatureTrend;
  isTopUsed?: boolean;
  onFeatureSelect?: (feature: string) => void;
  onSentimentSelect?: (kind: VoteKind) => void;
}) {
  const { feature, count, averageRating, up, down } = trend.stats;
  const { delta } = halfSplitDelta(trend.daily);
  const sparkPoints = bucketRatings(trend.daily, 6);

  return (
    <div className="grid grid-cols-[1fr_auto] items-center gap-x-4 gap-y-1 py-2.5 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1.6fr)_auto_auto_auto]">
      {/* Rank + feature name (clickable to drill down) */}
      <div className="flex min-w-0 items-center gap-2">
        <span className="w-5 shrink-0 text-right text-sm tabular-nums text-muted-foreground">{rank}</span>
        <div className="min-w-0 truncate text-sm font-medium">
          {onFeatureSelect ? (
            <button
              type="button"
              onClick={() => onFeatureSelect(feature)}
              title={`Filter feedback by "${feature}"`}
              className="truncate rounded text-left hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {feature}
            </button>
          ) : (
            feature
          )}
        </div>
        {isTopUsed && (
          <Badge variant="secondary" className="shrink-0" title="Feature with the most responses">
            Most used
          </Badge>
        )}
      </div>

      {/* Stacked sentiment bar — click a segment to reveal counts + filter by sentiment */}
      <SentimentBar
        up={up}
        down={down}
        total={count}
        interactive
        onSegmentSelect={onSentimentSelect}
      />

      {/* Average rating */}
      <div className="text-right text-sm tabular-nums text-warning-500">★ {averageRating.toFixed(1)}</div>

      {/* Response count */}
      <div className="text-right text-sm tabular-nums text-muted-foreground">{count}</div>

      {/* Trend sparkline + delta */}
      <div className="flex items-center justify-end gap-2">
        <Sparkline values={sparkPoints} min={1} max={5} aria-label={`${feature} rating trend`} />
        {delta !== undefined && delta !== null && (
          <DeltaBadge delta={delta} format={(n) => `${n > 0 ? '+' : ''}${n.toFixed(1)}`} />
        )}
      </div>
    </div>
  );
}
